import { Link } from "@inertiajs/react"
import { SeoHead } from "@/components/SeoHead"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { Section, Eyebrow } from "@/components/marketing/sections/Section"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"

type Breakdown = {
  question: string
  prompt: string
  answer_key: string
  answer_label: string
  points: number
  commentary: string
}

type Props = {
  token: string
  email: string
  name: string | null
  podcast_name: string | null
  podcast_url: string | null
  grade: string
  score: number | null
  pre_launch: boolean
  breakdown: Breakdown[]
  commentary: string | null
  created_at: string
}

const GRADE_COPY: Record<string, { headline: string; sub: string }> = {
  A: {
    headline: "Your show is doing what it's supposed to.",
    sub: "You're running the moves most expert podcasters never figure out. The work now is to systematize what's already compounding.",
  },
  B: {
    headline: "Most pieces are in place — one or two gaps are leaking value.",
    sub: "You're past the hard part. Close the gaps the breakdown calls out and the compound accelerates.",
  },
  C: {
    headline: "You have a show. You don't yet have the system around it.",
    sub: "The cadence is there, the strategy isn't. The fastest grade-jump is usually one or two of the moves below.",
  },
  D: {
    headline: "The podcast is happening. The leverage isn't.",
    sub: "Most expert podcasts stall here. The good news: the lifts are specific, not vague.",
  },
  F: {
    headline: "Right now, your show is content — not strategy.",
    sub: "The fundamentals aren't in place yet. The good news: every single one is fixable, and most of them are upstream of recording.",
  },
}

export default function PodcastScorecardResult(props: Props) {
  if (props.pre_launch) {
    return <PreLaunchResult {...props} />
  }
  return <GradedResult {...props} />
}

function PreLaunchResult({ email, commentary }: Props) {
  return (
    <MarketingLayout>
      <SeoHead title="Your Podcast Scorecard" description="Your personalized podcast strategy diagnostic from FullCast." noindex />
      <Section containerClassName="max-w-2xl">
        <Eyebrow>FullCast · Podcast Growth Partnership</Eyebrow>
        <h1 className="display-1 mt-4">Pre-launch — different conversation.</h1>
        <p className="mt-5 text-lg text-ink-body">
          {commentary || "You haven't started a podcast yet — the scorecard above is for shows that already exist. For pre-launch strategy, the move is different."}
        </p>
        <p className="mt-3 text-ink-muted">We sent a copy to {email}.</p>

        <div className="mt-10 rounded-2xl border border-hairline bg-surface p-6 sm:p-8">
          <h2 className="display-2">Want help building it from scratch?</h2>
          <p className="mt-3 text-ink-body">
            A Fit Call is a 30-minute conversation about what you'd want the show to do, who you'd want on it, and whether a podcast-led relationship strategy fits your business.
          </p>
          <div className="mt-6">
            <Button asChild size="lg">
              <Link href="/apply">Apply for a Fit Call</Link>
            </Button>
          </div>
        </div>
      </Section>
    </MarketingLayout>
  )
}

function GradedResult({ email, grade, score, breakdown, podcast_name }: Props) {
  const copy = GRADE_COPY[grade] || GRADE_COPY.F
  return (
    <MarketingLayout>
      <SeoHead title={`Your Podcast Scorecard — Grade ${grade}`} description="Your personalized podcast strategy diagnostic from FullCast." noindex />

      {/* Grade hero */}
      <Section containerClassName="max-w-3xl">
        <Eyebrow>FullCast · Podcast Growth Partnership</Eyebrow>
        <div className="mt-6 flex items-center gap-6 sm:gap-8">
          <div className="flex h-32 w-32 shrink-0 items-center justify-center rounded-2xl bg-accent text-accent-foreground sm:h-40 sm:w-40">
            <span className="text-7xl font-bold leading-none sm:text-8xl">{grade}</span>
          </div>
          <div>
            <div className="text-sm text-ink-muted">
              {podcast_name ? `${podcast_name} · ` : ""}{score}/100
            </div>
            <h1 className="display-2 mt-2">{copy.headline}</h1>
          </div>
        </div>
        <p className="mt-6 text-lg text-ink-body">{copy.sub}</p>
        <p className="mt-3 text-sm text-ink-muted">A copy is on its way to {email}.</p>
      </Section>

      {/* Per-question breakdown */}
      <Section className="bg-surface" containerClassName="max-w-3xl">
        <Eyebrow>The breakdown</Eyebrow>
        <h2 className="display-2 mt-4">Six dimensions, scored</h2>
        <p className="mt-3 text-ink-body">
          Each card shows what you answered, what it means, and where the points landed.
          Read the failing ones first — that's where the next move lives.
        </p>

        <div className="mt-8 space-y-5">
          {breakdown.map((b) => (
            <div
              key={b.question}
              className="rounded-xl border border-hairline bg-page p-6"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3>{b.prompt}</h3>
                  <p className="mt-2 text-ink-muted">
                    Your answer: <span className="text-ink-body">{b.answer_label}</span>
                  </p>
                </div>
                <Badge tone={b.points >= 15 ? "signal" : b.points >= 5 ? "muted" : "accent"}>
                  {b.points} pt{b.points === 1 ? "" : "s"}
                </Badge>
              </div>
              <p className="mt-4 text-ink-body">{b.commentary}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <Section containerClassName="max-w-2xl">
        <div className="rounded-2xl border border-hairline bg-surface p-6 sm:p-10">
          <Eyebrow>The next move</Eyebrow>
          <h2 className="display-2 mt-4">Want this run for you?</h2>
          <p className="mt-3 text-ink-body">
            A Fit Call is a 30-minute conversation about your offer, audience, and whether
            a Dream-50 guest strategy + done-for-you production fits where you are. No
            pitch deck. Just diagnostic.
          </p>
          <div className="mt-6">
            <Button asChild size="lg">
              <Link href="/apply">Apply for a Fit Call</Link>
            </Button>
          </div>
        </div>
      </Section>
    </MarketingLayout>
  )
}
